import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User, UserType } from 'app/core/user/user.types';
import { CmsAdminService } from 'app/modules/admin/cms/cms-admin.service';
import { environment } from 'app/core/environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
    ]
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    /** Avatar to show: the user's CMS staff photo if they have one, else their own avatar. */
    avatarSrc: string | null = null;

    /** The staff photo found for {@link _staffPhotoFor}; looked up once per signed-in user. */
    private _staffPhoto: string | null = null;
    private _staffPhotoFor: string | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _cms: CmsAdminService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                if (user?.id !== this._staffPhotoFor) {
                    this._staffPhoto = null;
                }
                // Default to the user's own avatar; upgrade to the CMS staff photo if there is one.
                this.avatarSrc = this._staffPhoto ?? user?.avatar ?? null;
                this._changeDetectorRef.markForCheck();

                // Once per user, not on every emission: a token refresh re-emits the
                // same user, and a failing lookup used to trigger a refresh — a loop.
                // The marketing operator and the simulation team can't read the CMS
                // (and have no staff photo).
                if (user?.id && user.id !== this._staffPhotoFor &&
                    user.type !== UserType.marketing_operator && user.type !== UserType.simulation_staff) {
                    this._staffPhotoFor = user.id;
                    this._loadStaffPhoto(user.id);
                }
            });
    }

    /** Use the logged-in person's CMS staff photo (matched by staffId), if present. */
    private _loadStaffPhoto(userId: string): void {
        this._cms.staff.list().subscribe({
            next: list => {
                const match = (list ?? []).find(s => s.staffId === userId);
                if (match?.imageUrl) {
                    this._staffPhoto = match.imageUrl.startsWith('http')
                        ? match.imageUrl
                        : `${environment.resourcesUrl}${match.imageUrl}`;
                    this.avatarSrc = this._staffPhoto;
                    this._changeDetectorRef.markForCheck();
                }
            },
            error: () => { /* keep the default avatar */ },
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }
}
